/**
 * El mecanismo de traduccion, sin diccionarios.
 *
 * Aqui esta COMO se traduce --la busqueda por clave, las dos formas del plural,
 * que hace una clave que falta-- y en `i18n.ts` estan los textos de la pantalla
 * del agente. Estaban juntos hasta que aparecio la pagina publica de alta, que
 * tiene sus propios textos y no puede cargar los del cotizador: son dos bundles
 * distintos a proposito [frontend/perfil.mjs], y un `import` de `i18n.ts`
 * meteria el diccionario entero de la aplicacion interna en la pagina que se le
 * pasa a un desconocido.
 *
 * Ninguna regla cambio al separarlo.
 */

export type Idioma = 'es' | 'en';

export const IDIOMAS: readonly Idioma[] = ['es', 'en'];

/** Español por defecto, como pide CLAUDE.md. */
export const IDIOMA_POR_DEFECTO: Idioma = 'es';

export function esIdioma(v: string | null | undefined): v is Idioma {
  return v === 'es' || v === 'en';
}

export function nombreIdioma(i: Idioma): string {
  return i === 'es' ? 'Español' : 'English';
}

function buscar(diccionario: unknown, clave: string): unknown {
  let nodo: unknown = diccionario;
  for (const parte of clave.split('.')) {
    if (typeof nodo !== 'object' || nodo === null) return undefined;
    nodo = (nodo as Record<string, unknown>)[parte];
  }
  return nodo;
}

/**
 * Un texto con dos formas segun la cantidad.
 *
 * Existe porque "1 tarifas están cargadas" delata que nadie miro la pantalla
 * con un solo resultado, y en el diagnostico de una busqueda vacia la cuenta
 * vale uno a menudo. En español ademas no basta con la 's': cambia el verbo.
 *
 * DOS FORMAS Y NO LAS SEIS DE CLDR. Español e ingles tienen la misma regla
 * --uno contra todo lo demas-- y son los dos idiomas de esta interfaz. El dia
 * que entre uno con dual o con formas para el cero, esto se queda corto y hay
 * que cambiarlo por una `Intl.PluralRules`; hasta entonces, seis categorias
 * serian cinco vacias en cada clave.
 */
interface FormasPlural {
  readonly uno: string;
  readonly otro: string;
}

function esFormasPlural(nodo: unknown): nodo is FormasPlural {
  return (
    typeof nodo === 'object' &&
    nodo !== null &&
    typeof (nodo as FormasPlural).uno === 'string' &&
    typeof (nodo as FormasPlural).otro === 'string'
  );
}

/**
 * La forma que toca, a partir del parametro `n`.
 *
 * `n` llega como CADENA, igual que los montos, y se compara como cadena: aqui
 * no hay ningun calculo que hacer y convertir a numero solo abriria la puerta a
 * un `NaN` silencioso. Sin `n`, se devuelve el plural: es lo que menos chirria
 * cuando quien llama se olvido de pasarlo.
 */
function elegirForma(formas: FormasPlural, params?: Record<string, string>): string {
  return params?.n === '1' ? formas.uno : formas.otro;
}

export type Traductor = (clave: string, params?: Record<string, string>) => string;

/**
 * Un traductor sobre un diccionario ya elegido.
 *
 * Una clave que falta se devuelve entre corchetes en vez de vacia. Una etiqueta
 * en blanco en una pantalla de tarifas es indistinguible de un dato ausente
 * --que aqui significa algo-- y ademas nadie la reporta. `[busqueda.pol]` en
 * pantalla es feo a proposito. Una clave que apunta a un objeto que NO son dos
 * formas de plural cae en el mismo sitio: es un error de diccionario, no un
 * texto.
 *
 * Sin respaldo al otro idioma: una clave que solo existe en es.json saldria en
 * español dentro de una pantalla en ingles y pareceria intencionada. Los dos
 * diccionarios tienen que estar completos, y el hueco visible es lo que lo
 * fuerza.
 */
export function crearTraductorDe(diccionario: unknown): Traductor {
  return (clave, params) => {
    const nodo = buscar(diccionario, clave);
    const texto = esFormasPlural(nodo)
      ? elegirForma(nodo, params)
      : typeof nodo === 'string'
        ? nodo
        : undefined;

    if (texto === undefined) return `[${clave}]`;
    if (params === undefined) return texto;

    return Object.entries(params).reduce(
      (acc, [k, v]) => acc.split(`{${k}}`).join(v),
      texto,
    );
  };
}
